import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { isEmpty } from 'lodash'
import {
    getCategories,
    getSubCategories,
    getFormats
} from "../../../app/store/slices/event/eventSetup";
import { getAllPublicEvents } from "../../../app/store/slices/event/event";

export default function useEventFilter() {
    const [filterState, setFilterState] = useState({}); // 👈 user's selected filter criteria
    const [openFilter, setOpenFilter] = useState(false);

    const [originalEvents, setOriginalEvents] = useState([]); // 👈 original/public events
    const [filterEvents, setFilterEvents] = useState([]);     // 👈 filtered events

    const [dateRange, setDateRange] = useState([null, null]); // 👈 selected date range
    const [eventsMonthInfo, setEventsMonthInfo] = useState({
        notFoundMonthName: null,
        foundMonthName: null
    });

    const [filterResultChips, setFilterResultChips] = useState([]); // 👈 user's selected filter chips
    const [isEventLoading, setIsEventLoading] = useState(false);

    const [error, setError] = useState(null);   // ⚠️ to manage the error

    const dispatch = useDispatch();
    const {
        categories,
        subCategories,
        formats
    } = useSelector((state) => state.eventSetup);

    /**
     * Function to return the date in YYYY-MM-DD format
     * 1. Get the year of month
     * 2. Get the month of date and add or pad with zero if < 10. means.
     * 3. Get the date of month and add or pad with zero if < 10. means.
     * 4. padStart means add a leading zero if the string is less than 2 characters long
     */
    function formatDate(date) {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };


    /**
     * Fetch events from the server
     */
    // useEffect(() => {
    //     async function fetchEvents() {
    //         try {
    //             setIsEventLoading(true);
    //             const response = await dispatch(getAllPublicEvents()).unwrap();
    //             const events = response?.data || [];
    //             setOriginalEvents(events);
    //             setFilterEvents(events);
    //         } catch (error) {
    //             setOriginalEvents([]);
    //             setFilterEvents([]);
    //             setError(error?.statusText)
    //         } finally {
    //             setIsEventLoading(false);
    //         }
    //     }
    //     fetchEvents();
    // }, [dispatch]);


    /**
    useEffect(() => {
        async function loadCurrentMonthEvents() {
            try {
                setIsEventLoading(true);

                const currentYear = new Date();

                const year = currentYear.getFullYear();
                const month = currentYear.getMonth();

                const startDate = new Date(year, month, 1);
                const endDate = new Date(year, month + 1, 0);

                const payload = {
                    start_date: formatDate(startDate),
                    end_date: formatDate(endDate)
                };

                // console.log("Payload -> ", payload);

                const response = await dispatch(getAllPublicEvents({
                    method: 'post',
                    payload: payload
                })).unwrap();

                const events = response?.data || [];
                setOriginalEvents(events);
                setFilterEvents(events);

            } catch (error) {
                setOriginalEvents([]);
                setFilterEvents([]);
                setError(error?.statusText)
            } finally {
                setIsEventLoading(false)
            }
        }
        loadCurrentMonthEvents();
    }, [dispatch])
    */


    /**
     * Load events for the current month
     * Immediate make another api call if events data is empty in first call / current month
     */
    useEffect(() => {
        async function loadCurrentMonthEvents() {
            try {
                setIsEventLoading(true);

                /**
                 * 1. Get the current date / month / year
                 * 2. Track if events are found or not
                 * 3. Store events in temp state
                 * 4. Track how many attempts are done
                 * 5. Store the not found month name
                 * 6. Store the found month
                 * 7. Max Attempts Limit Prevent infinite loop (3 Month max -> current + 2 more)
                 */
                let current = new Date();
                let found = false;
                let tempEvents = [];
                let attempts = 0;
                let notFoundMonthName = null;
                let foundMonthName = null;
                const maxAttempts = 3;


                /**
                 * While
                 *  1. Events are not found
                 *  2. Attempts are within a limit
                 * Then run the loop
                 */
                while (!found && attempts < maxAttempts) {

                    /**
                     * Current year and month
                     */
                    const year = current.getFullYear();
                    const month = current.getMonth();


                    /**
                     * Dynamic startDate and endDate of current month
                     */
                    const startDate = new Date(year, month, 1);
                    const endDate = new Date(year, month + 1, 0);


                    /**
                     * Dynamically generated payload
                     */
                    const payload = {
                        start_date: formatDate(startDate),
                        end_date: formatDate(endDate)
                    };


                    /**
                     * Call API
                     */
                    const response = await dispatch(getAllPublicEvents({
                        method: 'post',
                        payload: payload
                    })).unwrap();

                    /**
                     * Store events in temp state for further execution
                     */
                    const events = response?.data || [];


                    /**
                     * Keep last tried month (regardless of found or not)
                     */
                    const monthName = startDate.toLocaleString("default", { month: "long" });

                    /**
                     * if events length is greater than 0 then set events in state
                     * else move to next month
                     */
                    if (events.length > 0) {
                        found = true;
                        tempEvents = events;
                        foundMonthName = monthName;
                    } else {
                        notFoundMonthName = monthName;
                        current = new Date(year, month + 1, 1);
                        attempts++;
                    }
                }


                /**
                 * After loop ends, update state once
                 */
                if (found) {
                    setOriginalEvents(tempEvents);
                    setFilterEvents(tempEvents);
                } else {
                    setOriginalEvents([]);
                    setFilterEvents([]);
                }


                /**
                 * Update state with last tried month + found month
                 */
                setEventsMonthInfo({
                    notFoundMonthName,
                    foundMonthName
                });

            } catch (error) {
                setOriginalEvents([]);
                setFilterEvents([]);
                setError(error?.statusText)
            } finally {
                setIsEventLoading(false)
            }
        }
        loadCurrentMonthEvents();
    }, [dispatch])

    // console.log("Events Month Info -> ", eventsMonthInfo)
    // console.log("isEventLoading -> ", isEventLoading)


    /**
     * By default load current month events.
     * If no events found in current month then immediate call next api that contains next month start date and end date to load events
     * This function will be called only once when the component is mounted
     */
    /**
    async function loadCurrentMonthEvents() {
        try {
            setIsEventLoading(true);

            const currentYear = new Date();

            const year = currentYear.getFullYear();
            const month = currentYear.getMonth();

            const startDate = new Date(year, month, 1);
            const endDate = new Date(year, month + 1, 0);

            const payload = {
                start_date: formatDate(startDate),
                end_date: formatDate(endDate)
            };

            // console.log("Payload -> ", payload);

            const response = await dispatch(getAllPublicEvents({
                method: 'post',
                payload: payload
            })).unwrap();

            const events = response?.data || [];
            setOriginalEvents(events);
            setFilterEvents(events);

        } catch (error) {
            setOriginalEvents([]);
            setFilterEvents([]);
            setError(error?.statusText)
        } finally {
            setIsEventLoading(false)
        }
    }
     */


    /**
     * Load events for user selected month
     */
    async function loadNextMonthEvents(currentMonth) {
        try {
            setIsEventLoading(true);

            /**
             * Get month and year from the date object
             */
            const year = currentMonth.getFullYear();
            const month = currentMonth.getMonth();


            /**
             * Create start date (first day of month)
             * 1 means the first day of the month
             */
            const startDate = new Date(year, month, 1);


            /**
             * Create end date (last day of month)
             * 0 means the last day of the previous month
             */
            const endDate = new Date(year, month + 1, 0);


            /**
             * Creating dynamic payload to get month based events
             */
            const payload = {
                start_date: formatDate(startDate),
                end_date: formatDate(endDate)
            };

            // console.log("Fetching events for date range:", payload);

            const response = await dispatch(getAllPublicEvents({
                method: 'post',
                payload: payload
            })).unwrap();

            const events = response?.data || [];
            // setOriginalEvents(events);
            setFilterEvents(events);

        } catch (error) {
            console.error("Failed to load month events:", error);
        } finally {
            setIsEventLoading(false);
        }
    }


    /**
     * Load filter data if not received from server
     */
    useEffect(() => {
        if (!categories || categories.length === 0) {
            dispatch(getCategories());
        }
        if (!subCategories || subCategories.length === 0) {
            dispatch(getSubCategories());
        }
        if (!formats || formats.length === 0) {
            dispatch(getFormats());
        }
    }, [dispatch, categories, subCategories, formats]);


    /**
     * buildChips
     * WHY WE SEPARATE RADIO VS MULTI FIELDS:
     * - Radio (single‑select) fields (sortBy, category, price) store ONE object.
     *   We add at most one chip per group.
     * - Multi (checkbox) fields (subCategory, format) store an ARRAY of objects.
     *   We need a chip for every selected item.
     *
     * CHIP OBJECT SHAPE:
     * {
     *   key: unique string (group-id) for React rendering & easy removal
     *   group: which filter group it belongs to (e.g. 'category')
     *   id:   the underlying option id
     *   name: display label
     * }
     *
     * When a chip is deleted we use (group,id) to update filterState properly:
     * - For radio fields: delete the whole property.
     * - For multi fields: remove only that one item from the array.
     */
    const buildChips = useCallback((state) => {
        const chips = [];
        const radioFields = ["sortBy", "category", "price"];
        radioFields.forEach((group) => {
            const val = state[group];
            if (val?.id) {
                chips.push({
                    key: `${group}-${val.id}`,
                    group,
                    id: val.id,
                    name: val.name
                });
            }
        });

        const multiFields = ["subCategory", "format"];
        multiFields.forEach((group) => {
            (state[group] || []).forEach((item) => {
                if (item?.id) {
                    chips.push({
                        key: `${group}-${item.id}`,
                        group,
                        id: item.id,
                        name: item.name
                    });
                }
            });
        });

        return chips;
    }, []);


    /**
     * Get the selected category id from filter state
     */
    const selectedCategoryId = useMemo(() => {
        const cat = filterState.category;
        if (!cat) return null;
        return typeof cat === 'object' ? cat.id : cat;
    }, [filterState.category]);


    /**
     * Filter subcategories for selected category id
     */
    const filteredSubCategories = useMemo(() => {
        if (!selectedCategoryId) return [];
        return (subCategories || []).filter(sc => sc.category_id === selectedCategoryId);
    }, [subCategories, selectedCategoryId]);


    /**
     * Clean subCategory selection when category changes
     */
    useEffect(() => {
        setFilterState(prev => {
            const prevSelected = prev.subCategory || [];

            // if no category is selected then clean sub category else keep prev values
            if (!selectedCategoryId) {
                return prevSelected.length ? { ...prev, subCategory: [] } : prev;
            }


            // Find valid subCategory IDs by comparing with ids
            const validIds = prevSelected.filter(id => {
                const sc = (subCategories || []).find(s => s.id === id);
                return sc && sc.category_id === selectedCategoryId;
            });


            if (validIds.length !== prevSelected.length) {
                return { ...prev, subCategory: validIds };
            }

            return prev;
        });
    }, [selectedCategoryId, subCategories]);


    /**
     * Get the event date based on the used_schedule_id that backend will notify the frontend
     * This function will run for each event card
     */
    const getEventDate = useCallback((usedScheduleId) => {
        // return null if usedScheduleId not came
        if (!usedScheduleId) return null;

        // get all the event schedule
        const eventSchedule = filterEvents?.map(event => event?.schedule).flat() || [];

        // return null if event schedule is null
        if (!eventSchedule || eventSchedule.length === 0) return null;

        // store the date object which id is matched with useScheduleId
        const useDate = eventSchedule.find(slot => slot.id === usedScheduleId);

        // if date object not found then return null
        if (!useDate) return null;

        // return final date
        return useDate?.date;
    }, [filterEvents]);


    // console.log("Filter State -> ", JSON.stringify(filterState, null, 2))
    // console.log("Filter Result Chips -> ", JSON.stringify(filterResultChips, null, 2))
    // console.log("Current Month -> ", currentMonth);


    /**
     * Sync the filter result chips state with filter state when there are already selected chips in filter result chips array
     * To remove the selected chips from filterResultChips state when user removes from filter states.
     */
    useEffect(() => {
        if (filterResultChips.length > 0) {
            setFilterResultChips(buildChips(filterState));
        }
        // else do nothing (chips will be set by applyFilter)
    }, [filterState, buildChips, filterResultChips.length]);


    const eventFilterConfig = [
        {
            id: 'sortBy',
            label: 'Sort By',
            type: 'radio',
            options: [
                { id: 1, name: 'Date: Closest To Furthest' },
            ],
            shouldShowToolTip: false,
        },
        {
            id: 'category',
            label: 'Category',
            type: 'radio',
            options: categories || [],
            shouldShowToolTip: true,
            tooltipText: 'You can select only single option'
        },
        {
            id: 'subCategory',
            label: 'Sub Category',
            type: 'checkbox',
            options: filteredSubCategories ? filteredSubCategories : [],
            shouldShowToolTip: true,
            tooltipText: 'You can select only multiple option'
        },
        {
            id: 'format',
            label: 'Format',
            type: 'checkbox',
            options: formats || [],
            shouldShowToolTip: true,
            tooltipText: 'You can select only multiple option'
        },
        {
            id: 'price',
            label: 'Price',
            type: 'radio',
            options: [
                { id: 1, name: 'Free' },
                { id: 2, name: 'Paid' },
                // { id: 3, name: 'All' }
            ],
            shouldShowToolTip: false
        }
    ]


    const handleOpenFilter = useCallback(() => setOpenFilter(true), []);
    const handleCloseFilter = useCallback(() => setOpenFilter(false), []);


    /**
     * Clear all filters and reset the filter state
     * Set originalEvents on filter clear
     */
    const clearAllFilter = useCallback(() => {
        setFilterState({});
        setFilterResultChips([]);
        setFilterEvents(originalEvents);
    }, [originalEvents]);


    /**
     * Filter the events based on below conditions
     */
    const dateFilteredEvents = useMemo(() => {
        // If no start or end date is selected, just return all events
        if (!dateRange[0] || !dateRange[1]) return filterEvents;

        const [start, end] = dateRange;

        return filterEvents.filter(event => {
            const eventStart = event.start_date;
            const eventEnd = event.end_date;

            /**
             * Case 1: User selects a single date (start === end)
             * -------------------------------------------------
             * Include the event if that single date lies between the event’s 
             * start_date and end_date (inclusive).
             * 
             * Example:
             *   Event: 20–26 Aug
             *   User: [22, 22] → included (22 lies inside event range)
             * 
             *   Event: 20–20 Aug
             *   User: [20, 20] → included (same date)
             * 
             *   Event: 20–20 Aug
             *   User: [22, 22] → excluded (22 not inside event)
             */
            if (start === end) {
                return eventStart <= start && eventEnd >= start;
            }

            /**
             * Case 2: User selects a date range (start !== end)
             * -------------------------------------------------
             * We include the event if its range overlaps with the user’s range.
             * 
             * Overlap rule:
             *   eventStart <= userEnd AND eventEnd >= userStart
             * 
             * Example:
             *   Event: 20–26 Aug
             *   User: [22, 24] → included (event covers 22–24)
             * 
             *   Event: 20–20 Aug
             *   User: [22, 24] → excluded (event outside user range)
             */
            return eventStart <= end && eventEnd >= start;
        });
    }, [filterEvents, dateRange]);


    /**
     * Apply filters to the event list
     * This function will be called when the user clicks the "Apply Filter" button
     * It will fetch the filtered events from the server and update the event list accordingly.
     */
    const applyFilter = useCallback(async () => {
        try {
            setIsEventLoading(true);

            const categoryId = filterState.category?.id || null;
            const subCategoriesIds = filterState.subCategory?.map(subCat => subCat.id) || [];
            const formatsIds = filterState.format?.map(format => format.id) || [];
            const priceId = filterState.price?.id || null;


            const filterCriteria = {};
            if (categoryId) filterCriteria.categories = categoryId;
            if (subCategoriesIds.length > 0) filterCriteria.subcategories = subCategoriesIds;
            if (formatsIds.length > 0) filterCriteria.formats = formatsIds;
            if (priceId) filterCriteria.price = priceId;

            // console.log("Filter Criteria -> ", filterCriteria);

            // Call getAllPublicEvents with POST method and filter criteria
            const response = await dispatch(getAllPublicEvents({
                method: 'post',
                payload: filterCriteria
            })).unwrap();

            // console.log("Response -> ", response);

            setOpenFilter(false);
            setFilterEvents(response?.data || []);
            setFilterResultChips(buildChips(filterState));
        } catch (error) {
            // If filtering fails, fall back to original events
            setFilterEvents(originalEvents);
        } finally {
            setIsEventLoading(false);
        }
    }, [
        dispatch,
        filterState,
        buildChips,
        originalEvents,
        setFilterEvents,
        setFilterResultChips,
        setOpenFilter,
        setIsEventLoading
    ]);


    /**
     * Remove a selected filter chip
     */
    const removeSelectedFilterChip = useCallback((chip) => {
        setFilterResultChips(prev => prev.filter(c => c.key !== chip.key));
        setFilterState(prev => {
            const { group, id } = chip;
            // multi-select
            if (['subCategory', 'format'].includes(group)) {
                const remaining = (prev[group] || []).filter(item => item.id !== id);
                if (remaining.length === 0) {
                    const next = { ...prev };
                    delete next[group];
                    return next;
                }
                return { ...prev, [group]: remaining };
            }
            // single select
            const next = { ...prev };
            delete next[group];
            return next;
        });

        // 👇 After removing, if no chips left → show original events
        setTimeout(() => {
            setFilterResultChips(chips => {
                if (chips.length === 0) {
                    setFilterEvents(originalEvents);
                }
                return chips;
            });
        }, 0);
    }, [originalEvents, filterState]);


    /**
     * Disable the apply filter button on below conditions
     * To prevent unnecessary backend calls without request body
     */
    const shouldDisableApplyFilterBtn = useMemo(() => {

        if (isEmpty(filterState)) return true

        if (
            filterState.category === null &&
            filterState.price === null &&
            filterState.subCategory.length === 0 &&
            filterState.format.length === 0
        ) {
            return true;
        }

        return false;
    }, [filterState])


    // function removeSelectedFilterChip(chip) {
    //     // Remove the chip from displayed results
    //     setFilterResultChips(prev =>
    //         prev.filter(c => c.key !== chip.key)
    //     );

    //     // Update the filter state based on chip type
    //     setFilterState(prev => {
    //         const newState = { ...prev };
    //         const { group, id } = chip;

    //         // Handle multi-select fields (checkboxes)
    //         if (group === 'subCategory' || group === 'format') {
    //             // Keep all items except the one being removed
    //             newState[group] = (prev[group] || []).filter(item =>
    //                 item.id !== id
    //             );
    //         }
    //         // Handle single-select fields (radio buttons)
    //         else {
    //             // Simply remove the entire property
    //             delete newState[group];
    //         }

    //         return newState;
    //     });
    // }


    return {
        isEventLoading,
        filterEvents: dateFilteredEvents,
        eventFilterConfig,
        filterState,
        setFilterState,
        clearAllFilter,
        openFilter,
        handleOpenFilter,
        handleCloseFilter,
        applyFilter,
        filterResultChips,
        removeSelectedFilterChip,
        setDateRange,
        getEventDate,
        error,
        shouldDisableApplyFilterBtn,
        loadNextMonthEvents,
        eventsMonthInfo
    }
}